import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router-dom';
import { BookOpen, Share2, FileText, Copy, Printer, Check, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SharedStudyPage() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadSharedContent();
  }, [shareId]);

  const loadSharedContent = async () => {
    try {
      const response = await axios.get(`${API}/share/${shareId}`);
      setData(response.data);
    } catch (err) {
      setError('This study is not available or has been made private.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1B3A6B]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-16 h-16 text-[#475569] mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-[#1B3A6B] mb-2">Content Not Found</h1>
          <p className="text-[#475569]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="shared-study-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E2DFD8] print:hidden">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {data.logo_url ? (
                <img src={data.logo_url} alt="Logo" className="w-10 h-10 object-contain" />
              ) : (
                <div className="w-10 h-10 bg-[#1B3A6B] rounded-xl flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="font-heading text-lg font-bold text-[#1B3A6B]">
                  {data.church_name || 'Bible Study Tool'}
                </h1>
                <p className="text-xs text-[#475569]">Shared Study</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="btn-secondary px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
              <button
                onClick={handlePrint}
                className="btn-secondary px-4 py-2 rounded-lg text-sm flex items-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-2xl shadow-[0_8px_32px_rgba(27,58,107,0.06)] p-12">
          {/* Branding Header */}
          {(data.logo_url || data.church_name) && (
            <div className="text-center mb-8 pb-8 border-b border-[#E2DFD8]">
              {data.logo_url && (
                <img src={data.logo_url} alt="Logo" className="h-16 mx-auto mb-4" />
              )}
              {data.church_name && (
                <p className="text-[#B8860B] font-medium uppercase tracking-wider text-sm">
                  {data.church_name}
                </p>
              )}
            </div>
          )}

          <div className="preview-content font-document">
            <ReactMarkdown>{data.content}</ReactMarkdown>
          </div>
        </div>

        <div className="text-center mt-8 text-sm text-[#475569] print:hidden">
          <p>Created with <span className="font-semibold text-[#1B3A6B]">Bible Study Tool</span></p>
          <p className="mt-1">
            Want to create your own? <a href="/" className="text-[#B8860B] hover:underline">Get started free</a>
          </p>
        </div>
      </main>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white; }
          .print\\:hidden { display: none !important; }
        }
      `}</style>
    </div>
  );
}
